import React, { useEffect, useMemo, useReducer, useState } from 'react';
import { useLocation, useParams } from 'react-router';
import { DownloadIcon } from 'lucide-react';
import { createPortal } from 'react-dom';
import {
  BreadCrumbs,
  CustomTable,
  FilterSection,
  SelectionActionBar,
} from '../../../../../components';
import type { ColumnDef } from '../../../../../components';
import { usePageHeader } from '../../../user';
import { FileDropdownOptions, KBTableColumnData } from '../../data/constants';
import StyledTooltip from '../../../../../components/tooltip';
import { DraftIcon, GeminiAiIcon } from '../../../../../assets/images';
import { FolderListReducer, initialStateFolderListReducer } from '../../kb.reducer';
import { deleteFile, downloadFile, fileForParticularFolder } from '../../services/kb';
import {
  FolderListAction,
  FolderListReducerProps,
  SharedFileTableProps,
  TableFunctions,
} from '../../kb.props';
import makeToast from '../../../../../components/toaster';
import { formatTimestamp, truncateData } from '../../../../../services';
import Logout from '../../../../../auth/protected/session-expire-modal';
import EditFile from './edit-file';
import UploadKnowledgeBaseFolder from '../../components/upload-kb';
import DeleteKnowledgeBaseFolder from '../../components/delete-kb';

type FileTableRow = SharedFileTableProps & { file_owner?: string };

function FolderTable() {
  const [componentState, dispatch] = useReducer<
    React.Reducer<FolderListReducerProps, FolderListAction>
  >(FolderListReducer, initialStateFolderListReducer);
  const { folderName } = useParams();
  const { state } = useLocation();
  const { folderId } = state || {};
  const [editError, SetEditError] = useState<string | null>('');
  const [tableData, setTableData] = useState<FileTableRow[]>([]);
  const [fileTypeFilter, setFileTypeFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [tableFunctions, setTableFunctions] = useState<TableFunctions>({
    limit: Number(process.env.REACT_APP_ROWS_PER_PAGE),
    offset: 0,
    sort_col: 'created_at',
    sort_order: 'DESC',
    search: '',
    filters: [{ type: 'file_type', value: [] }],
  });
  const [publicUserId, setPublicUserId] = useState<number>(0);

  const permissionsJson = localStorage.getItem('permissions');
  const userId = Number(localStorage.getItem('user_id')) || 0;
  const permittedAccess = permissionsJson
    ? JSON.parse(permissionsJson).find(
        (permission: { module: string }) => permission.module === 'knowledge-base',
      )?.data || []
    : [];

  useEffect(() => {
    fileForParticularFolder(tableFunctions, folderId)
      .then((res) => {
        if (res.status === 200) {
          setPublicUserId(res.data.data.user_id);
          setTableData(res.data.data.files || []);
          dispatch({
            type: 'SET_TOTAL_TABLE_DATA',
            payload: res.data.data.total_files,
          });
          setTimeout(() => {
            dispatch({ type: 'SET_LOADING', payload: false });
          }, 1000);
        } else if (res.status === 401) {
          dispatch({ type: 'SET_LOGOUT', payload: true });
        }
      })
      .catch(() => {
        dispatch({ type: 'SET_LOGOUT', payload: true });
      });
  }, [tableFunctions, componentState.dataToDelete, componentState.dataUpdated]);

  useEffect(() => {
    const selectedValue = fileTypeFilter === 'all' ? [] : [fileTypeFilter];
    setTableFunctions((prevState) => ({
      ...prevState,
      filters: prevState.filters.map((filter) =>
        filter.type === 'file_type' ? { ...filter, value: selectedValue } : filter,
      ),
    }));
  }, [fileTypeFilter]);

  const addUploadButton = publicUserId === userId;

  const handleUploadKb = () => {
    dispatch({ type: 'TOGGLE_UPLOAD_MODAL', payload: true });
  };

  const handleOnDeleteClick = (data: number) => {
    dispatch({ type: 'TOGGLE_DELETE_MODAL', payload: true });
    dispatch({ type: 'SET_DATA_TO_DELETE', payload: [data] });
  };

  const handleOnEditClick = (name: string, id: number) => {
    const trimmedName = name.replace(/\.[^/.]+$/, '');
    dispatch({ type: 'TOGGLE_EDIT_MODAL', payload: true });
    dispatch({ type: 'FILE_TO_EDIT', payload: { id, name: trimmedName } });
  };

  const handleCancelClick = () => {
    SetEditError('');
    dispatch({ type: 'TOGGLE_EDIT_MODAL', payload: false });
    dispatch({ type: 'TOGGLE_UPLOAD_MODAL', payload: false });
    dispatch({ type: 'TOGGLE_DELETE_MODAL', payload: false });
  };

  const handleDeleteFileClick = () => {
    deleteFile(folderId, componentState?.dataToDelete ?? [])
      .then((res) => {
        if (res.status === 200) {
          dispatch({ type: 'SET_DATA_TO_DELETE', payload: [] });
          makeToast('success', 'File deleted successfully!');
          handleCancelClick();
        }
      })
      .catch(() => {
        makeToast('error', 'Something went wrong!');
      });
  };

  const handleDeleteCheckBoxClick = (id: number) => {
    let updatedSelection = [...(componentState?.dataToDelete ?? [])];
    if (updatedSelection.includes(id)) {
      updatedSelection = updatedSelection.filter((item) => item !== id);
    } else {
      updatedSelection.push(id);
    }
    dispatch({ type: 'SET_DATA_TO_DELETE', payload: updatedSelection });
  };

  const handleMultiDataDelete = () => {
    dispatch({ type: 'TOGGLE_DELETE_MODAL', payload: true });
  };

  const handleClearSelection = () => {
    dispatch({ type: 'SET_DATA_TO_DELETE', payload: [] });
    dispatch({ type: 'SET_SELECT_ALL', payload: false });
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const ids = tableData.map((row) => Number(row.id));
      const combined = [...new Set([...(componentState?.dataToDelete ?? []), ...ids])];
      dispatch({ type: 'SET_DATA_TO_DELETE', payload: combined });
    } else {
      const idsToRemove = new Set(tableData.map((row) => Number(row.id)));
      const remaining = (componentState?.dataToDelete ?? []).filter((id) => !idsToRemove.has(id));
      dispatch({ type: 'SET_DATA_TO_DELETE', payload: remaining });
    }
  };

  const saveFile = (blob: Blob, fileName: string) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleOnDownloadClick = (id: number, name: string) => {
    downloadFile(id, folderId)
      .then((res) => {
        if (res.status === 200) {
          const blob = new Blob([res.data], {
            type: res.headers['content-type'],
          });
          const contentDisposition = res.headers['content-disposition'];
          let fileName = name;
          if (contentDisposition) {
            const [, extractedFileName] = contentDisposition.match(/filename="?([^"]+)"?/) || [];
            if (extractedFileName) {
              fileName = extractedFileName;
            }
          }
          saveFile(blob, fileName);
          setTimeout(() => {
            makeToast('success', 'File Downloaded Successfully!');
          }, 1000);
        } else if (res.status === 404) {
          makeToast('error', 'File not found!');
        }
      })
      .catch(() => {
        makeToast('error', 'Something went Wrong!');
      });
  };

  const handleOffSetValue = (page: number) => {
    setTableFunctions((prevState) => ({
      ...prevState,
      offset: (page - 1) * prevState.limit,
    }));
  };

  const handleClearFilters = () => {
    setTableFunctions((prevState) => ({
      ...prevState,
      search: '',
      offset: 0,
      filters: [{ type: 'file_type', value: [] }],
    }));
    setFileTypeFilter('all');
    setSearchQuery('');
  };

  const handleCancelUploadClick = () => {
    dispatch({ type: 'TOGGLE_UPLOAD_MODAL', payload: false });
  };

  const totalCount = componentState.totalTableData ?? 0;
  const totalPages = Math.ceil(totalCount / tableFunctions.limit) || 1;
  const currentPage = Math.floor(tableFunctions.offset / tableFunctions.limit) + 1;
  const hasActiveFilters =
    fileTypeFilter !== 'all' || !!(tableFunctions.search && tableFunctions.search.length > 0);

  const fileTypeOptions = [
    { value: 'all', label: 'All File Types' },
    ...(FileDropdownOptions[0]?.options || []),
  ];

  const columns: ColumnDef<FileTableRow>[] = [
    {
      key: 'name',
      header: KBTableColumnData[0],
      width: 'w-[35%]',
      headerIcon: (
        <div className="w-10 h-10 rounded-full bg-[#e0f1f8] flex items-center justify-center flex-shrink-0">
          <img src={DraftIcon} height={10} width={10} alt="draft" />
        </div>
      ),
      render: (row: FileTableRow) => (
        <div className="flex items-center gap-3 min-w-0 ml-2">
          {permittedAccess.includes('delete') && addUploadButton && (
            <input
              type="checkbox"
              className="border-[#101828] border-[2px] w-[15px] h-[15px]"
              onChange={() => handleDeleteCheckBoxClick(Number(row.id))}
              checked={componentState?.dataToDelete?.includes(Number(row.id))}
            />
          )}
          <div className="w-10 h-10 rounded-full bg-[#e0f1f8] flex items-center justify-center flex-shrink-0">
            <img src={DraftIcon} height={10} width={10} alt="draft" />
          </div>
          <StyledTooltip title={row.name}>
            <span>{truncateData(row.name, 70)}</span>
          </StyledTooltip>
        </div>
      ),
    },
    {
      key: 'file_owner',
      header: KBTableColumnData[1],
      width: 'w-[20%]',
      render: (row: FileTableRow) => (
        <StyledTooltip title={row.file_owner || row.sharedBy || ''}>
          <span>{truncateData(row.file_owner || row.sharedBy || '', 15)}</span>
        </StyledTooltip>
      ),
    },
    {
      key: 'created_at',
      header: KBTableColumnData[2],
      width: 'w-[25%]',
      render: (row: FileTableRow) => <span>{formatTimestamp(row.created_at)}</span>,
    },
  ];

  const tableActions =
    permittedAccess.includes('update') ||
    permittedAccess.includes('delete') ||
    permittedAccess.includes('view')
      ? {
          onView: permittedAccess.includes('view')
            ? (row: FileTableRow) => handleOnDownloadClick(Number(row.id), row.name)
            : undefined,
          viewActionLabel: 'Download',
          onEdit:
            addUploadButton && permittedAccess.includes('update')
              ? (row: FileTableRow) => handleOnEditClick(row.name, Number(row.id))
              : undefined,
          onDelete:
            addUploadButton && permittedAccess.includes('delete')
              ? (row: FileTableRow) => handleOnDeleteClick(Number(row.id))
              : undefined,
          ActionIcon: <DownloadIcon className="w-4 h-4" />,
        }
      : undefined;

  const headerLeftContent = useMemo(
    () => (
      <div className="flex items-center gap-2 w-full">
        <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[hsl(var(--primary))]/20 to-[hsl(var(--primary))]/5 flex items-center justify-center shadow-sm flex-shrink-0">
          <img src={GeminiAiIcon} alt="Knowledge Base" className="w-6 h-6" />
        </div>
        <div className="flex flex-col gap-1 min-w-0">
          <StyledTooltip title={folderName!}>
            <p className="font-bold text-2xl">{truncateData(folderName!, 40)}</p>
          </StyledTooltip>
          <div className="breadcrumbs">
            <BreadCrumbs />
          </div>
        </div>
      </div>
    ),
    [],
  );

  usePageHeader({ headerLeftContent });

  return (
    <>
      <div>
        <FilterSection
          searchValue={searchQuery}
          onSearchChange={(v: string) => {
            setSearchQuery(v);
            setTableFunctions((prev) => ({
              ...prev,
              search: v,
              offset: 0,
            }));
            dispatch({ type: 'SET_LOADING', payload: true });
          }}
          searchPlaceholder="Search files..."
          filters={[
            {
              value: fileTypeFilter,
              onChange: (v: string) => {
                setFileTypeFilter(v);
                setTableFunctions((prev) => ({ ...prev, offset: 0 }));
              },
              options: fileTypeOptions,
              placeholder: 'Filter by file type',
              clearValue: 'all',
              showClear: false,
            },
          ]}
          hasActiveFilters={hasActiveFilters}
          onClearFilters={handleClearFilters}
        />

        {permittedAccess.includes('delete') && addUploadButton && (
          <SelectionActionBar
            selectedCount={componentState?.dataToDelete?.length ?? 0}
            onClear={handleClearSelection}
            onDelete={handleMultiDataDelete}
          />
        )}

        <CustomTable<FileTableRow>
          headerRightContent={
            permittedAccess?.includes('create') && addUploadButton ? (
              <button
                type="button"
                onClick={handleUploadKb}
                className="px-4 py-2 text-sm font-medium text-[#F48B00] border-1 border-[#F48B00] hover:bg-[#F48B00]/10 rounded-lg transition-colors"
              >
                Upload Files
              </button>
            ) : undefined
          }
          selectionConfig={
            permittedAccess.includes('delete') && addUploadButton
              ? {
                  selectedIds: componentState?.dataToDelete ?? [],
                  getRowId: (row: FileTableRow) => Number(row.id),
                  onSelectAll: handleSelectAll,
                }
              : undefined
          }
          columns={columns}
          data={tableData}
          rowKey="id"
          loading={componentState.loading ?? false}
          totalCount={totalCount}
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={(page: number) => handleOffSetValue(page)}
          title="Files"
          emptyMessage="No files found."
          loadingMessage="Loading files..."
          actions={tableActions}
        />
      </div>

      <EditFile
        open={componentState.editModalOpen ?? false}
        dataToEdit={
          typeof componentState?.dataToEdit === 'object' &&
          componentState?.dataToEdit !== null &&
          'id' in componentState.dataToEdit &&
          'name' in componentState.dataToEdit
            ? (componentState.dataToEdit as { id: number; name: string })
            : { id: 0, name: '' }
        }
        dispatcher={dispatch}
        handleCancelClick={handleCancelClick}
        editError={editError}
        SetEditError={SetEditError}
      />

      <UploadKnowledgeBaseFolder
        service="system"
        open={componentState.uploadModalOpen}
        dispatcher={dispatch}
        idToUpload={0}
        handleCancelUploadClick={handleCancelUploadClick}
      />
      <DeleteKnowledgeBaseFolder
        open={componentState.deleteModalOpen}
        handleCancelClick={handleCancelClick}
        handleDeleteKb={handleDeleteFileClick}
        type={componentState?.dataToDelete?.length === 1 ? 'File' : 'Files'}
      />

      {componentState.logout && createPortal(<Logout />, document.body)}
    </>
  );
}

export default FolderTable;
